/**
 * Minimal form state bound to a shared zod schema.
 *
 * The server validates every body against these same schemas, so validating
 * client-side is purely to give faster feedback — never a substitute. Two
 * behaviours matter for usability:
 *
 *  - A field's error appears only after it has been touched or a submit has
 *    been attempted, so a pristine form is not a wall of red.
 *  - `fields` from the server's error envelope are merged in, which is how
 *    rules the client cannot check (an address already in use, a wrong current
 *    password) land next to the right input.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import type { z } from 'zod';
import { ApiError } from '@/lib/api';

type Values = Record<string, unknown>;
type Errors = Record<string, string>;

export interface ZodForm<T extends Values> {
  values: T;
  errors: Errors;
  /** Error not attributable to a single field; render at the top of the form. */
  formError: string | null;
  submitting: boolean;
  setValue: <K extends keyof T>(name: K, value: T[K]) => void;
  /** onBlur handler that starts showing a field's error. */
  touch: (name: keyof T) => void;
  /** Everything an input needs, including accessibility wiring. */
  fieldProps: (name: keyof T & string) => {
    name: string;
    id: string;
    'aria-invalid': boolean;
    'aria-describedby': string | undefined;
    onBlur: () => void;
  };
  errorFor: (name: keyof T & string) => string | undefined;
  handleSubmit: (event: React.FormEvent) => void;
  reset: (next?: Partial<T>) => void;
  setFormError: (message: string | null) => void;
}

export function useZodForm<T extends Values, Output>(options: {
  schema: z.ZodType<Output, z.ZodTypeDef, unknown>;
  initialValues: T;
  onSubmit: (parsed: Output) => Promise<void> | void;
}): ZodForm<T> {
  const { schema, initialValues, onSubmit } = options;

  const [values, setValues] = useState<T>(initialValues);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [serverErrors, setServerErrors] = useState<Errors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [attempted, setAttempted] = useState(false);

  // `onSubmit` is usually an inline closure; a ref keeps handleSubmit stable
  // without asking every caller to memoise it.
  const submitRef = useRef(onSubmit);
  submitRef.current = onSubmit;

  const clientErrors = useMemo<Errors>(() => {
    const result = schema.safeParse(values);
    if (result.success) return {};

    const collected: Errors = {};
    for (const issue of result.error.issues) {
      const key = issue.path.join('.');
      // Keep the first message per field; later ones are usually redundant.
      if (key && !collected[key]) collected[key] = issue.message;
    }
    return collected;
  }, [schema, values]);

  const visible = useCallback(
    (name: string) => attempted || touched[name] === true,
    [attempted, touched],
  );

  const errors = useMemo<Errors>(() => {
    const merged: Errors = {};
    for (const [key, message] of Object.entries(clientErrors)) {
      if (visible(key)) merged[key] = message;
    }
    // A server verdict outranks a client guess: it saw the real state.
    return { ...merged, ...serverErrors };
  }, [clientErrors, serverErrors, visible]);

  const setValue = useCallback<ZodForm<T>['setValue']>((name, value) => {
    setValues((current) => ({ ...current, [name]: value }));
    // The server's objection was about the old value; drop it as they retype.
    setServerErrors((current) => {
      if (!(name in current)) return current;
      const { [name as string]: _removed, ...rest } = current;
      return rest;
    });
  }, []);

  const touch = useCallback((name: keyof T) => {
    setTouched((current) => ({ ...current, [name as string]: true }));
  }, []);

  const errorFor = useCallback((name: string) => errors[name], [errors]);

  const fieldProps = useCallback(
    (name: keyof T & string) => ({
      name,
      id: `field-${name}`,
      'aria-invalid': Boolean(errors[name]),
      'aria-describedby': errors[name] ? `field-${name}-error` : undefined,
      onBlur: () => touch(name),
    }),
    [errors, touch],
  );

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      setAttempted(true);
      setFormError(null);
      setServerErrors({});

      const result = schema.safeParse(values);
      if (!result.success) return;

      setSubmitting(true);
      void (async () => {
        try {
          await submitRef.current(result.data);
        } catch (error) {
          if (error instanceof ApiError) {
            if (error.fields) {
              const mapped: Errors = {};
              for (const [key, messages] of Object.entries(error.fields)) {
                const first = messages[0];
                if (first) mapped[key] = first;
              }
              setServerErrors(mapped);
              // Field messages carry the detail; a banner too would be noise.
              if (Object.keys(mapped).length > 0) return;
            }
            setFormError(error.message);
            return;
          }
          setFormError('Something went wrong. Please try again.');
        } finally {
          setSubmitting(false);
        }
      })();
    },
    [schema, values],
  );

  const reset = useCallback(
    (next?: Partial<T>) => {
      setValues({ ...initialValues, ...next });
      setTouched({});
      setServerErrors({});
      setFormError(null);
      setAttempted(false);
    },
    [initialValues],
  );

  return {
    values,
    errors,
    formError,
    submitting,
    setValue,
    touch,
    fieldProps,
    errorFor,
    handleSubmit,
    reset,
    setFormError,
  };
}
